/* Мост загрузок фото, видео и звука для движков шаблона (engine-kit, план M3 §2 S2; circle K2, photo P2, motion K1/K2, text K2, reels K3,
   collage K-SL, mcard K2). Голосовой upload-bridge.ts не трогается. Рамка BotCore.upload проверяет тип и вес по строке accept SRV и отдаёт
   файлы сюда; дальше — проверки по политике поля формы, лист согласия, одна очередь загрузок и файлы в рамке движка. Правила:
   - сервис заперт (заявка, проверка владельца, создание) — причина словами; прав на материалы нет — лист согласия, файлы ждут в нём
     («Продолжить загрузку» — тот же выбор заново, закрыли лист — файлы не грузятся);
   - тип: пустой или общий тип файла уточняется по расширению (.mov → video/quicktime; звук — confirmAudioType), затем accept → max_bytes →
     размеры фото (min_width/min_height) → длина звука и видео (min/max_duration_ms); размеры и длину может прочитать сам движок (meta);
   - ОДНА очередь (FIFO) на все загрузки кадра: svc.actions.upload (поля формы) и svc.actions.uploadRaw (JSON-ключи контракта экрана) —
     сервис загружает по одному файлу (order-service.ts upload: uploading), второй вызов он молча отклонил бы;
   - транспорт не подтвердил (UploadTransportError): файл остаётся, его id ждёт «Продолжить загрузку» (resume — тот же id, без нового слота);
   - окончательный отказ (UploadRejectedError, 4xx): файл уходит из рамки (drop), адрес blob освобождается, причина — текст сервиса
     (uploadFailureText); загрузка поля снимается;
   - готово — один GET /app/uploads/{id} (проверки снимка и media: длительность, размеры, звук) уходит экрану (update);
   - лоток слотов (photo, photo_2…photo_5; JSON-список upload_list): записи по порядку, слоты всегда подряд — убрать, поменять местами,
     заменить переписывают ключи слотов (inputs и uploadIds) без новой загрузки (inputsFor — то же для своей сборки входов);
   - upload(key, file) — загрузка вне лотков (свой референс BotRef, ref-bridge.ts, план M3 S6 L-R): та же очередь, согласие, замок и
     политика ключа, id получает вызывающий (входы и рамку мост не трогает); отказ — с reject_reason сервера и текстом;
   - restore — превью из повтора («Изменить», «История»): фото — uploadPreview (владелец до и после, тип картинки, вес), звук — как
     upload-bridge.ts, видео — превью сервер не отдаёт (решение Q7-video): загрузку снимаем и просим загрузить заново; не вышло (решение
     владельца Q7) — загрузка снимается, прежний id не используется; 30 с на всё.
   Адреса blob файлов создаёт и освобождает мост (рамка их только показывает). Устаревшие чтения отбрасывает живость записи. */
import { endpoints } from '../../../api';
import { uiFormat, uiText } from '../../../i18n';
import { sampleFile } from '../../clone/sample-file';
import { recordingWav } from '../../tts/recording-wav';
import { mmss } from '../../voice/voice-model';
import { studioFields, type StudioForm } from '../studio-api';
import { openConsentSheet, type ConsentOptions, type ConsentSheet } from './consent-sheet';
import {
  uploadFailureText,
  uploadPurpose,
  referenceUse,
  type OrderDerived,
  type OrderService,
  type OrderSnapshot,
  type RawPolicy,
} from './order-service';
import { uploadCheck, type UploadCheck } from './upload-checks';

export type MediaKind = 'audio' | 'photo' | 'video';
/** Политика загрузки поля формы (upload: accept, max_bytes, min_width/height, min/max_duration_ms) */
export interface MediaPolicy {
  key: string;
  kind: MediaKind;
  accept: string[];
  maxBytes: number;
  minWidth: number | null;
  minHeight: number | null;
  minMs: number | null;
  maxMs: number | null;
  /** Строка ограничений рамки: её рамка говорит, когда тип не подходит */
  note: string;
  /** JSON-ключ контракта экрана (value_encoding json): загрузка через uploadRaw, id в inputs пишет мост; иначе — поле формы (upload) */
  raw: RawPolicy | null;
}
/** Что движок уже прочитал в файле (BC.files: размеры, длина) — мост не читает заново */
export interface MediaMeta {
  width?: number;
  height?: number;
  durationMs?: number;
}
/** consent — ждёт листа согласия; queued — в очереди; uploading — грузится; pending — транспорт не подтвердил («Продолжить загрузку»);
    held — загрузка не началась (сервис был заперт) — тоже ждёт «Продолжить загрузку»; restoring — превью повтора читается; ready — id готов */
export type MediaState = 'queued' | 'uploading' | 'pending' | 'held' | 'restoring' | 'ready';
export interface MediaItem {
  /** Номер записи моста (не id загрузки): запись та же, когда слоты меняются местами */
  readonly id: string;
  /** Ключ слота сейчас (поле формы или JSON-ключ) */
  key: string;
  kind: MediaKind;
  /** Файл; у восстановленной записи — превью сервера */
  file: File | null;
  /** Адрес blob для рамки ('' — превью нет) */
  url: string;
  name: string;
  size: number;
  type: string;
  meta: MediaMeta;
  state: MediaState;
  /** id готовой загрузки */
  uploadId: string | null;
  /** id неподтверждённой загрузки (транспорт) */
  pendingId: string | null;
  /** Проверки сервера после готовности (null — ответа нет) */
  check: UploadCheck | null;
  /** Запись пришла из повтора */
  restored: boolean;
  /** Запись с микрофона */
  rec?: boolean;
}
/** Слоты лотка: поля формы по порядку (photo, photo_2…) — или JSON-список одного ключа (upload_list, photo_upload_ids) */
export type TraySpec = string[] | { list: string; max: number };
export interface MediaTray {
  readonly name: string;
  list(): MediaItem[];
  /** Ключи слотов (у списка — один ключ) */
  keys(): string[];
  /** Сколько файлов вмещает лоток */
  max(): number;
  /** Файлы в свободные слоты по порядку (at — заменить запись at одним файлом); metas — прочитанное движком, по файлам */
  pickSlots(files: File[], at?: number, metas?: Array<MediaMeta | undefined>): Promise<void>;
  /** Убрать запись i: слоты правее съезжают, загрузки не повторяются */
  removeSlot(i: number): void;
  /** Убрать запись по её id */
  remove(id: string): void;
  /** Новый порядок записей (перестановка id нынешних записей) */
  order(ids: string[]): void;
  /** Заменить файл записи i (новая загрузка в тот же слот) */
  replaceAt(i: number, file: File, meta?: MediaMeta): Promise<void>;
  /** Входы с ключами слотов по порядку записей (только готовые; без новой загрузки) */
  inputsFor(prev: Record<string, string>): Record<string, string>;
  /** Записи из id загрузок повтора (по умолчанию — из входов сервиса), превью и проверки */
  restore(ids?: string[]): Promise<void>;
}
/** Ответ загрузки вне лотков: id готовой загрузки; отказ (reason — reject_reason сервера, если он назван; text — причина словами:
    политика поля, замок, текст сервиса); cancelled — лист согласия закрыли, запись сняли (signal), кадр сменился (cancel) */
export type MediaUpload = { id: string } | { refused: true; reason: string | null; text: string } | { cancelled: true };
export interface MediaBridgeOptions {
  svc: OrderService;
  owner(): number | undefined;
  /** Причина словами в рамке ключа (UP.say: дрожь и строка ограничений) */
  say(text: string, key: string): void;
  /** Политика ключа; по умолчанию — mediaPolicy(форма сервиса, ключ) */
  policy?(key: string): MediaPolicy | null;
  /** Файл встал в рамку или лоток (до загрузки) */
  put?(item: MediaItem, tray: string): void;
  /** Файл ушёл из рамки (отказ сервера, превью повтора не открылось); null — показать было нечего */
  drop?(item: MediaItem | null, key: string, tray: string): void;
  /** Состояние записи сменилось (загрузилась, проверки сервера пришли, ключ слота сдвинулся) */
  update?(item: MediaItem, tray: string): void;
  /** Свой лист согласия: go — тот же выбор заново, cancel — файлы не грузятся. По умолчанию — openConsentSheet с правами сервиса */
  consent?(go: () => void, cancel: () => void): void;
  /** Тексты и поле согласия для листа по умолчанию (consent-sheet.ts texts, field, consentField) */
  consentWith?(): Partial<Pick<ConsentOptions, 'texts' | 'field' | 'fieldOn' | 'setField'>>;
  /** Запись JSON-ключа (uploadRaw): value — закодированное значение или null (снять). По умолчанию — inputs[key] */
  write?(key: string, value: string | null): void;
}
export interface MediaBridge {
  /** Одиночная рамка: файл в ключ (прежний файл ключа уходит) */
  pick(key: string, file: File, meta?: MediaMeta): Promise<void>;
  /** Запись с микрофона: в WAV (как VoiceOrderForm), затем pick без уточнения типа */
  recorded(key: string, blob: Blob, maxMs: number): Promise<void>;
  /** Убрать файл одиночной рамки (загрузка поля снимается) */
  remove(key: string): void;
  /** Файл одиночной рамки */
  item(key: string): MediaItem | null;
  /** Превью одиночной рамки из повтора */
  restore(key: string, uploadId: string): Promise<void>;
  /** Лоток слотов (один на набор ключей; повторный вызов — тот же лоток) */
  slots(spec: TraySpec): MediaTray;
  pickSlots(files: File[], keys: string[], at?: number, metas?: Array<MediaMeta | undefined>): Promise<void>;
  removeSlot(i: number, keys: string[]): void;
  /** «Продолжить загрузку»: неподтверждённые (тот же id) и не начатые файлы снова в очередь */
  resume(): Promise<void>;
  /** Есть файл без готовой загрузки (в очереди, грузится, ждёт продолжения, читается превью) */
  pending(): boolean;
  /** Ничего не грузится, но файл ждёт «Продолжить загрузку» */
  stalled(): boolean;
  /** Отложенная запись ключей (сервис был заперт) — после разблокировки; экран зовёт на каждом зафиксированном виде */
  sync(): void;
  /** Загрузка вне лотков (свой референс BotRef через ref-bridge.ts, план M3 S6 L-R): та же очередь, лист согласия, замок и политика
      ключа key, но id во входы мост не пишет и рамке (put/update/drop/say) ничего не говорит — ответ получает вызывающий.
      Транспорт не подтвердил — запись ждёт «Продолжить загрузку» (resume, тот же id), ответ приходит после неё.
      signal — запись больше не нужна: не начатая загрузка снимается, ответ — { cancelled } */
  upload(key: string, file: File, meta?: MediaMeta, signal?: AbortSignal): Promise<MediaUpload>;
  /** Забыть все файлы, закрыть лист согласия, прервать чтения, освободить адреса (кадр сменился, экран закрылся) */
  cancel(): void;
}

const MB = 1024 * 1024;
/** Пределы рамки шаблона по виду (UPK bot-core.js), если поле их не публикует */
const KIND_MB: Record<MediaKind, number> = { photo: 10, video: 200, audio: 50 };
/** accept по виду, если поле его не публикует (как StudioPhotoTray) */
const KIND_ACCEPT: Record<MediaKind, string[]> = {
  photo: ['image/jpeg', 'image/png', 'image/webp'],
  video: ['video/mp4', 'video/quicktime'],
  audio: ['audio/mpeg', 'audio/wav', 'audio/mp4'],
};
const EXT: Record<MediaKind, Record<string, string>> = {
  photo: { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', heic: 'image/heic', heif: 'image/heif' },
  video: { mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime', qt: 'video/quicktime', webm: 'video/webm' },
  audio: { mp3: 'audio/mpeg', wav: 'audio/wav', m4a: 'audio/mp4', mp4: 'audio/mp4', ogg: 'audio/ogg', webm: 'audio/webm' },
};
const ALIAS: Record<string, string> = {
  'image/jpg': 'image/jpeg',
  'image/pjpeg': 'image/jpeg',
  'video/x-quicktime': 'video/quicktime',
  'video/x-m4v': 'video/mp4',
};
const NAMES: Record<string, string> = {
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/webp': 'WebP',
  'video/mp4': 'MP4',
  'video/quicktime': 'MOV',
  'video/webm': 'WebM',
  'audio/mpeg': 'MP3',
  'audio/wav': 'WAV',
  'audio/mp4': 'M4A',
  'audio/ogg': 'OGG',
  'audio/webm': 'WebM',
};
/** Типы превью загрузки, которые рамка показывает (как useStudioPhotoPreviews и VoiceOrderForm) */
const PREVIEW: Record<MediaKind, string[]> = {
  photo: ['image/jpeg', 'image/png', 'image/webp'],
  video: [],
  audio: ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/ogg', 'audio/webm'],
};
/** Текст сервиса для неподтверждённой загрузки (транспорт): только её можно продолжить тем же id */
const TRANSPORT = uploadFailureText(null);
const RESTORE_FAIL: Record<MediaKind, string> = {
  photo: 'Не удалось открыть фото. Загрузите его заново.',
  video: 'Не удалось открыть видео. Загрузите его заново.',
  audio: 'Не удалось открыть запись для прослушивания. Загрузите её заново.',
};
const num = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null);

/** Подпись типов и веса рамки: «JPG или PNG, до 10 МБ» (как строка ограничений шаблона) */
export function mediaNote(accept: string[], maxBytes: number): string {
  const list = [...new Set(accept.map(mime => NAMES[mime] ?? mime.split('/')[1]?.toUpperCase()).filter(Boolean))];
  const kinds = list.length > 1 ? uiFormat('{0} или {1}', list.slice(0, -1).join(', '), list.at(-1)) : (list[0] ?? '');
  return [kinds, uiFormat('до {0}', uiFormat('{0} МБ', Math.round(maxBytes / MB)))].filter(Boolean).join(', ');
}
/** Политика загрузки ключа из формы (поля шаблона и контракта экрана); вид document и неопубликованный ключ — null.
    over — то, что экран задаёт сам (видео без предела длины: фрагмент выбирается полосой; своя строка ограничений). */
export function mediaPolicy(
  form: StudioForm | null | undefined,
  key: string,
  over: Partial<Omit<MediaPolicy, 'key' | 'raw'>> = {},
): MediaPolicy | null {
  const field = studioFields(form).find(item => item.key === key && item.upload);
  const upload = field?.upload;
  if (!form || !field || !upload || !['photo', 'audio', 'video'].includes(upload.kind)) return null;
  const kind = upload.kind as MediaKind;
  const policy = upload as unknown as Record<string, unknown>;
  const accept =
    Array.isArray(upload.accept) && upload.accept.length ? upload.accept.filter(item => typeof item === 'string') : KIND_ACCEPT[kind];
  const maxBytes = num(upload.max_bytes) ?? KIND_MB[kind] * MB;
  const purpose = uploadPurpose(upload);
  const use = purpose === 'reference' ? (upload.use ?? referenceUse(form)) : undefined;
  const base: MediaPolicy = {
    key,
    kind,
    accept,
    maxBytes,
    minWidth: num(policy.min_width),
    minHeight: num(policy.min_height),
    minMs: num(policy.min_duration_ms),
    maxMs: num(upload.max_duration_ms),
    note: mediaNote(accept, maxBytes),
    raw: field.value_encoding === 'json' ? { kind, purpose, ...(use ? { use } : {}) } : null,
  };
  const merged = { ...base, ...over, accept: over.accept ?? base.accept };
  /* свои типы или вес без своей строки — строка ограничений по ним */
  return over.note === undefined && (over.accept || over.maxBytes)
    ? { ...merged, note: mediaNote(merged.accept, merged.maxBytes) }
    : merged;
}
/** accept для рамки BotCore.upload: типы политики и их расширения (файл без типа — iOS, Android — проходит рамку по расширению) */
export function acceptString(policy: Pick<MediaPolicy, 'kind' | 'accept'>): string {
  const ext = Object.entries(EXT[policy.kind])
    .filter(([, mime]) => policy.accept.includes(mime))
    .map(([name]) => '.' + name);
  return [...policy.accept, ...ext].join(',');
}

/** Тип файла по политике: общий или пустой тип — по расширению; звук — confirmAudioType. Не вышло — null */
function confirmType(file: File, kind: MediaKind): File | null {
  if (kind === 'audio') {
    try {
      return sampleFile.confirmAudioType(file);
    } catch {
      return null;
    }
  }
  const raw = file.type.split(';')[0].trim().toLowerCase();
  const alias = ALIAS[raw] ?? raw;
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  const type = alias && alias !== 'application/octet-stream' ? alias : EXT[kind][extension];
  if (!type || !type.startsWith(kind === 'photo' ? 'image/' : 'video/')) return null;
  return type === file.type ? file : new File([file], file.name, { type, lastModified: file.lastModified });
}
/** Размеры картинки (10 с) или null */
function readImageSize(file: File): Promise<{ width: number; height: number } | null> {
  const url = URL.createObjectURL(file);
  return new Promise<{ width: number; height: number } | null>(resolve => {
    const image = new Image();
    const timer = setTimeout(() => resolve(null), 10000);
    image.onload = () => {
      clearTimeout(timer);
      resolve(image.naturalWidth && image.naturalHeight ? { width: image.naturalWidth, height: image.naturalHeight } : null);
    };
    image.onerror = () => {
      clearTimeout(timer);
      resolve(null);
    };
    image.src = url;
  }).finally(() => URL.revokeObjectURL(url));
}
/** Длина звука (sampleFile.readDurationMs) или видео (метаданные <video>, 10 с), мс; не прочиталась — null */
function readDuration(file: File, kind: MediaKind, signal?: AbortSignal): Promise<number | null> {
  if (kind === 'audio') return sampleFile.readDurationMs(file, signal).catch(() => null);
  const url = URL.createObjectURL(file);
  return new Promise<number | null>(resolve => {
    const video = document.createElement('video');
    const timer = setTimeout(() => resolve(null), 10000);
    video.preload = 'metadata';
    video.muted = true;
    video.onloadedmetadata = () => {
      clearTimeout(timer);
      const ms = Math.round(video.duration * 1000);
      resolve(Number.isFinite(ms) && ms > 0 ? ms : null);
    };
    video.onerror = () => {
      clearTimeout(timer);
      resolve(null);
    };
    video.src = url;
  }).finally(() => {
    URL.revokeObjectURL(url);
  });
}
/** Чтение и уточнение файлов (подменяются в тестах, как sampleFile) */
export const mediaFile = { confirmType, readImageSize, readDuration };

/** Причина, по которой файл не годится политике (тексты рамки), или null */
async function problem(file: File, policy: MediaPolicy, meta: MediaMeta): Promise<string | null> {
  if (!policy.accept.includes(file.type)) return policy.note;
  if (file.size > policy.maxBytes) return uiFormat('Файл больше {0} МБ, загрузите меньше', Math.round(policy.maxBytes / MB));
  if (policy.kind === 'photo' && (policy.minWidth || policy.minHeight)) {
    if (!meta.width || !meta.height) {
      const size = await mediaFile.readImageSize(file);
      if (!size) return uiText('Файл не читается, загрузите другой');
      meta.width = size.width;
      meta.height = size.height;
    }
    if ((policy.minWidth && meta.width! < policy.minWidth) || (policy.minHeight && meta.height! < policy.minHeight))
      return uiFormat(
        'Фото меньше {0} × {1} пикселей, загрузите крупнее',
        policy.minWidth ?? policy.minHeight,
        policy.minHeight ?? policy.minWidth,
      );
  }
  if (policy.kind !== 'photo' && (policy.minMs || policy.maxMs)) {
    if (!meta.durationMs) {
      const ms = await mediaFile.readDuration(file, policy.kind);
      if (!ms) return uiText('Файл не читается, загрузите другой');
      meta.durationMs = ms;
    }
    const audio = policy.kind === 'audio';
    if (policy.maxMs && meta.durationMs > policy.maxMs)
      return uiFormat(audio ? 'Запись длиннее {0}, загрузите короче' : 'Видео длиннее {0}, загрузите короче', mmss(policy.maxMs / 1000));
    if (policy.minMs && meta.durationMs < policy.minMs)
      return uiFormat(audio ? 'Запись короче {0}, загрузите длиннее' : 'Видео короче {0}, загрузите длиннее', mmss(policy.minMs / 1000));
  }
  return null;
}
function lockedText(s: OrderSnapshot, d: OrderDerived) {
  if (d.submissionBlocked) return s.integrityError || uiText('Проверяем доступность');
  if (s.intent) return uiText('Прежний заказ ещё не завершён. Сначала проверьте его результат.');
  return uiText(s.uploadingFile ? 'Загружаем файл' : 'Создаём');
}
/** У записи есть id загрузки, который стоит в слоте: готова или её превью из повтора ещё читается */
const inSlot = (entry: MediaItem) => !!entry.uploadId && (entry.state === 'ready' || entry.state === 'restoring');
const listOf = (raw: string | undefined): string[] => {
  try {
    const value = JSON.parse(raw ?? '[]') as unknown;
    return Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string' && !!id) : [];
  } catch {
    return [];
  }
};

interface Tray extends MediaTray {
  entries: MediaItem[];
  /** detached — загрузки вне лотков (upload): ключи и входы не пишутся, рамке ничего не говорится */
  mode: 'fields' | 'raw' | 'list' | 'detached';
  /** Ключи слотов надо переписать, когда сервис отопрётся */
  dirty: boolean;
  rewrite(): void;
}

export function createMediaBridge(o: MediaBridgeOptions): MediaBridge {
  const { svc } = o;
  const trays = new Map<string, Tray>();
  const live = new Set<MediaItem>();
  const owned = new Set<string>();
  const queue: Array<{ item: MediaItem; tray: Tray }> = [];
  let flight: { item: MediaItem; tray: Tray } | null = null;
  let draining = false;
  let seq = 0;
  let consentSheet: ConsentSheet | null = null;
  /** Чтение превью повтора по лотку (у каждого лотка своё) */
  const readers = new Map<Tray, AbortController>();
  /** Ответы загрузок вне лотков (upload) по записи */
  const waiters = new Map<MediaItem, (value: MediaUpload) => void>();
  const reply = (item: MediaItem, value: MediaUpload) => {
    const done = waiters.get(item);
    if (!done) return;
    waiters.delete(item);
    done(value);
  };
  let epoch = 0;

  const policyOf = (key: string) => (o.policy ? o.policy(key) : mediaPolicy(svc.get().form, key));
  const now = () => {
    const s = svc.get();
    return { s, d: svc.derive(s) };
  };
  /** Заперто не своей загрузкой: заявка, проверка, создание (своя загрузка в полёте правку лотка не запирает) */
  const opLocked = () => {
    const { s, d } = now();
    return d.submissionBlocked || !!s.intent || (s.busy && !flight);
  };
  const url = (blob: Blob) => {
    const value = URL.createObjectURL(blob);
    owned.add(value);
    return value;
  };
  const free = (value: string) => {
    if (value && owned.delete(value)) URL.revokeObjectURL(value);
  };
  function forget(item: MediaItem) {
    live.delete(item);
    free(item.url);
  }
  const keyOf = (tray: Tray) => tray.keys()[0] ?? tray.name;
  const writeKey = (key: string, value: string | null) => {
    if (o.write) return o.write(key, value);
    svc.setters.inputs(current => {
      if (value === null ? !(key in current) : current[key] === value) return current;
      const next = { ...current };
      if (value === null) delete next[key];
      else next[key] = value;
      return next;
    });
  };

  /* ---- лоток ---- */
  function makeTray(name: string, spec: TraySpec, detached = false): Tray {
    const list = Array.isArray(spec) ? null : spec;
    const fieldKeys = Array.isArray(spec) ? spec : [spec.list];
    const tray: Tray = {
      name,
      entries: [],
      /* по форме сейчас: лоток могли создать до прихода формы */
      get mode() {
        if (detached) return 'detached';
        return list ? 'list' : fieldKeys.length === 1 && policyOf(fieldKeys[0])?.raw ? 'raw' : 'fields';
      },
      dirty: false,
      list: () => tray.entries.slice(),
      keys: () => fieldKeys.slice(),
      max: () => (list ? list.max : fieldKeys.length),
      pickSlots: (files, at, metas) => add(tray, files, at, metas),
      removeSlot(i) {
        const item = tray.entries[i];
        if (item) tray.remove(item.id);
      },
      remove(id) {
        const item = tray.entries.find(entry => entry.id === id);
        if (!item) return;
        if (opLocked()) return void o.say(lockedText(now().s, now().d), keyOf(tray));
        tray.entries.splice(tray.entries.indexOf(item), 1);
        forget(item);
        tray.rewrite();
      },
      order(ids) {
        const next = ids.map(id => tray.entries.find(entry => entry.id === id));
        if (next.length !== tray.entries.length || next.some(entry => !entry) || new Set(ids).size !== ids.length) return;
        if (opLocked()) return void o.say(lockedText(now().s, now().d), keyOf(tray));
        tray.entries.splice(0, tray.entries.length, ...(next as MediaItem[]));
        tray.rewrite();
      },
      replaceAt: (i, file, meta) => add(tray, [file], i, [meta]),
      inputsFor(prev) {
        const next = { ...prev };
        if (tray.mode === 'detached') return next;
        if (tray.mode === 'list') {
          const ids = tray.entries.filter(inSlot).map(entry => entry.uploadId!);
          if (ids.length) next[fieldKeys[0]] = JSON.stringify(ids);
          else delete next[fieldKeys[0]];
          return next;
        }
        if (tray.mode === 'raw') {
          const ready = tray.entries[0] && inSlot(tray.entries[0]) ? tray.entries[0].uploadId : null;
          if (ready) next[fieldKeys[0]] = encodeRaw(fieldKeys[0], ready);
          else delete next[fieldKeys[0]];
          return next;
        }
        fieldKeys.forEach((key, i) => {
          const entry = tray.entries[i];
          if (entry && inSlot(entry)) next[key] = entry.uploadId!;
          else delete next[key];
        });
        return next;
      },
      restore: ids => restoreTray(tray, ids),
      rewrite() {
        /* загрузки вне лотков: id получает вызывающий, ключи и входы не пишутся */
        if (tray.mode === 'detached') {
          tray.dirty = false;
          return;
        }
        /* своя загрузка лотка в полёте: ключи перепишутся после неё (сервис сам пишет id в ключ, с которым её начали) */
        if (flight?.tray === tray) {
          tray.dirty = true;
          return;
        }
        /* заявка или проверка владельца: входы принадлежат заказу — их не трогаем (повтор восстановит экран) */
        const { d, s } = now();
        tray.dirty = false;
        if (d.submissionBlocked || s.intent) return;
        tray.entries.forEach((entry, i) => {
          const key = tray.mode === 'fields' ? fieldKeys[i] : fieldKeys[0];
          if (entry.key !== key) {
            entry.key = key;
            o.update?.(entry, tray.name);
          }
        });
        if (tray.mode !== 'fields') {
          const value = tray.inputsFor(s.inputs)[fieldKeys[0]];
          writeKey(fieldKeys[0], value ?? null);
          return;
        }
        svc.setters.inputs(current => {
          const next = tray.inputsFor(current);
          return fieldKeys.every(key => current[key] === next[key]) ? current : next;
        });
        svc.setters.uploadIds(current => {
          const next = { ...current };
          fieldKeys.forEach((key, i) => {
            const entry = tray.entries[i];
            const id = entry && inSlot(entry) ? entry.uploadId : entry?.state === 'pending' ? entry.pendingId : null;
            if (id) next[key] = id;
            else delete next[key];
          });
          return fieldKeys.every(key => current[key] === next[key]) ? current : next;
        });
      },
    };
    return tray;
  }
  const encodeRaw = (key: string, id: string) => {
    const field = studioFields(svc.get().form).find(item => item.key === key);
    return field?.value_encoding === 'json' ? JSON.stringify(id) : id;
  };
  function trayOf(spec: TraySpec): Tray {
    const name = Array.isArray(spec) ? spec.join(',') : `list:${spec.list}`;
    let tray = trays.get(name);
    if (!tray) {
      tray = makeTray(name, spec);
      trays.set(name, tray);
    }
    return tray;
  }

  /* ---- выбор файлов ---- */
  async function add(tray: Tray, files: File[], at?: number, metas: Array<MediaMeta | undefined> = [], recorded = false) {
    const key = keyOf(tray);
    const policy = policyOf(key);
    if (!policy || !files.length) return;
    if (opLocked()) return o.say(lockedText(now().s, now().d), key);
    if (!svc.get().rights) {
      waitConsent(() => void add(tray, files, at, metas, recorded));
      return;
    }
    const mine = epoch;
    const replace = at !== undefined && at >= 0 && at < tray.max();
    const room = replace ? 1 : tray.max() - tray.entries.length;
    if (tray.max() > 1 && policy.kind === 'photo' && files.length > Math.max(room, 0))
      o.say(uiFormat('Можно до {0} фото', tray.max()), key);
    if (room <= 0) return;
    const accepted: Array<{ file: File; meta: MediaMeta }> = [];
    let reason: string | null = null;
    for (const [index, raw] of files.slice(0, room).entries()) {
      const file = recorded ? raw : mediaFile.confirmType(raw, policy.kind);
      const meta: MediaMeta = { ...(metas[index] ?? {}) };
      const why = file ? await problem(file, policy, meta) : policy.note;
      if (mine !== epoch) return;
      if (why) reason ??= why;
      else accepted.push({ file: file!, meta });
    }
    if (reason) o.say(reason, key);
    if (!accepted.length) return;
    if (opLocked()) return o.say(lockedText(now().s, now().d), key);
    for (const { file, meta } of accepted) {
      const item: MediaItem = {
        id: `m${++seq}`,
        key,
        kind: policy.kind,
        file,
        url: url(file),
        name: file.name,
        size: file.size,
        type: file.type,
        meta,
        state: 'queued',
        uploadId: null,
        pendingId: null,
        check: null,
        restored: false,
        ...(recorded ? { rec: true } : {}),
      };
      live.add(item);
      if (replace && tray.entries[at!]) {
        const old = tray.entries[at!];
        tray.entries[at!] = item;
        forget(old);
      } else if (replace && at! < tray.entries.length) tray.entries[at!] = item;
      else tray.entries.push(item);
      item.key = tray.mode === 'fields' ? tray.keys()[tray.entries.indexOf(item)] : tray.keys()[0];
      o.put?.(item, tray.name);
      queue.push({ item, tray });
    }
    tray.rewrite();
    await drain();
  }
  /** Лист согласия: один на мост, новый выбор отменяет прежний (как consent-sheet); stop — лист закрыли без согласия */
  function waitConsent(go: () => void, stop?: () => void) {
    const cancel = () => {
      consentSheet = null;
      stop?.();
    };
    const next = () => {
      consentSheet = null;
      go();
    };
    if (o.consent) return o.consent(next, cancel);
    consentSheet = openConsentSheet({
      rights: () => svc.get().rights,
      locked: () => svc.derive().locked,
      setRights: on => svc.setters.rights(on),
      onContinue: next,
      onClose: cancel,
      ...o.consentWith?.(),
    });
  }

  /* ---- загрузка вне лотков (upload) ---- */
  async function detach(key: string, file: File, meta: MediaMeta = {}, signal?: AbortSignal): Promise<MediaUpload> {
    const policy = policyOf(key);
    if (!policy || signal?.aborted) return { cancelled: true };
    const refused = (text: string): MediaUpload => ({ refused: true, reason: null, text });
    if (opLocked()) return refused(lockedText(now().s, now().d));
    if (!svc.get().rights)
      return new Promise<MediaUpload>(resolve =>
        waitConsent(
          () => void detach(key, file, meta, signal).then(resolve),
          () => resolve({ cancelled: true }),
        ),
      );
    const mine = epoch;
    const typed = mediaFile.confirmType(file, policy.kind);
    const read: MediaMeta = { ...meta };
    const why = typed ? await problem(typed, policy, read) : policy.note;
    if (mine !== epoch || signal?.aborted) return { cancelled: true };
    if (!typed || why) return refused(why ?? policy.note);
    if (opLocked()) return refused(lockedText(now().s, now().d));
    let tray = trays.get(`detached:${key}`);
    if (!tray) {
      tray = makeTray(`detached:${key}`, [key], true);
      trays.set(tray.name, tray);
    }
    const item: MediaItem = {
      id: `m${++seq}`,
      key,
      kind: policy.kind,
      file: typed,
      url: '',
      name: typed.name,
      size: typed.size,
      type: typed.type,
      meta: read,
      state: 'queued',
      uploadId: null,
      pendingId: null,
      check: null,
      restored: false,
    };
    const own = tray;
    const done = new Promise<MediaUpload>(resolve => waiters.set(item, resolve));
    live.add(item);
    own.entries.push(item);
    signal?.addEventListener(
      'abort',
      () => {
        /* в полёте сервис не прервать: ответ отбросится, запись уйдёт после него */
        if (flight?.item !== item && own.entries.includes(item)) {
          own.entries.splice(own.entries.indexOf(item), 1);
          forget(item);
        }
        reply(item, { cancelled: true });
      },
      { once: true },
    );
    queue.push({ item, tray: own });
    void drain();
    return done;
  }

  /* ---- очередь ---- */
  async function drain() {
    if (draining) return;
    draining = true;
    try {
      while (queue.length) {
        const job = queue.shift()!;
        if (!live.has(job.item) || job.item.state !== 'queued' || !job.tray.entries.includes(job.item)) continue;
        if (job.tray.dirty) job.tray.rewrite();
        const mine = epoch;
        flight = job;
        try {
          await send(job.item, job.tray);
        } finally {
          flight = null;
        }
        /* cancel() во время загрузки: сервис прежнего кадра не трогаем */
        if (mine === epoch) job.tray.rewrite();
      }
    } finally {
      draining = false;
    }
  }
  async function send(item: MediaItem, tray: Tray) {
    const policy = policyOf(keyOf(tray));
    const { s, d } = now();
    const detached = tray.mode === 'detached';
    /* рамке — только о записях лотков; загрузка вне лотков отвечает вызывающему (reply) */
    const tell = () => {
      if (!detached) o.update?.(item, tray.name);
    };
    /* сервис заперт или прав нет: загрузка не начнётся — файл ждёт «Продолжить загрузку» */
    if (!policy || d.locked || !s.rights || !s.form) {
      item.state = 'held';
      tell();
      return;
    }
    const key = item.key;
    item.state = 'uploading';
    tell();
    let id: string | null = null;
    let reason: string | null = null;
    if (tray.mode === 'fields') {
      const resume = !!item.pendingId && s.uploadIds[key] === item.pendingId;
      /* чужой ждущий id в ключе: сервис проверил бы его вместо нового файла */
      if (!resume && (s.uploadIds[key] || s.inputs[key])) {
        svc.setters.inputs(current => {
          const next = { ...current };
          delete next[key];
          return next;
        });
        svc.setters.uploadIds(current => {
          const next = { ...current };
          delete next[key];
          return next;
        });
      }
      const ok = await svc.actions.upload(key, resume ? null : item.file);
      if (!live.has(item)) return;
      if (ok) id = svc.get().inputs[key] ?? null;
      else if (ok === false) {
        item.state = 'held';
        tell();
        return;
      } else item.pendingId = svc.get().uploadIds[key] ?? null;
    } else {
      const rawPolicy = detached ? detachedPolicy(key, policy) : (policy.raw ?? { kind: policy.kind });
      const answer = await svc.actions.uploadRaw(rawPolicy, item.pendingId ? null : item.file, item.pendingId ?? undefined);
      if (!live.has(item)) return;
      if ('id' in answer) id = answer.id;
      else {
        item.pendingId = answer.pendingId ?? null;
        reason = answer.reason ?? null;
      }
    }
    /* вне лотков: запись сняли, пока она грузилась (signal) — ответ никому не нужен */
    const leave = () => {
      if (tray.entries.includes(item)) tray.entries.splice(tray.entries.indexOf(item), 1);
      forget(item);
    };
    if (detached && !waiters.has(item)) return leave();
    if (id) {
      item.state = 'ready';
      item.uploadId = id;
      item.pendingId = null;
      if (detached) {
        leave();
        return reply(item, { id });
      }
      o.update?.(item, tray.name);
      void checkOf(item, tray);
      return;
    }
    const { error } = svc.get();
    /* без текста — запрос прервали (кадр сняли): это не окончательный ответ */
    if (!error || error === TRANSPORT) {
      item.state = error ? 'pending' : 'held';
      tell();
      return;
    }
    /* окончательный отказ: файл уходит, загрузка снимается */
    if (detached) {
      leave();
      return reply(item, { refused: true, reason, text: error });
    }
    tray.entries.splice(tray.entries.indexOf(item), 1);
    forget(item);
    o.drop?.(item, key, tray.name);
    o.say(error, key);
  }
  /** Назначение и use загрузки вне лотков — по полю формы (как svc.actions.upload у поля шаблона) */
  function detachedPolicy(key: string, policy: MediaPolicy): RawPolicy {
    if (policy.raw) return policy.raw;
    const form = svc.get().form;
    const upload = studioFields(form).find(item => item.key === key && item.upload)?.upload;
    const purpose = upload ? uploadPurpose(upload) : 'order_input';
    const use = purpose === 'reference' && form ? (upload?.use ?? referenceUse(form)) : undefined;
    return { kind: policy.kind, purpose, ...(use ? { use } : {}) };
  }
  async function checkOf(item: MediaItem, tray: Tray) {
    if (!item.uploadId) return;
    try {
      const value = await endpoints.uploadStatus(item.uploadId);
      if (!live.has(item) || (value.id && value.id !== item.uploadId)) return;
      item.check = uploadCheck(value);
      o.update?.(item, tray.name);
    } catch {
      /* проверок нет — ничего не выдумываем */
    }
  }

  /* ---- превью из повтора ---- */
  async function restoreTray(tray: Tray, given?: string[]) {
    const key = keyOf(tray);
    const policy = policyOf(key),
      owner = o.owner();
    if (!policy || !owner) return;
    const { s } = now();
    const ids = (
      given ??
      (tray.mode === 'list' ? listOf(s.inputs[key]) : tray.mode === 'raw' ? [rawId(key, s.inputs[key])] : tray.keys().map(k => s.inputs[k]))
    ).filter((id): id is string => !!id);
    tray.entries.splice(0).forEach(forget);
    queue.splice(0, queue.length, ...queue.filter(job => job.tray !== tray));
    readers.get(tray)?.abort();
    readers.delete(tray);
    if (!ids.length) return;
    const mine = epoch;
    const request = new AbortController();
    readers.set(tray, request);
    let timedOut = false;
    const deadline = window.setTimeout(() => {
      timedOut = true;
      request.abort();
    }, 30000);
    const items = ids.slice(0, tray.max()).map((id, i): MediaItem => {
      const item: MediaItem = {
        id: `m${++seq}`,
        key: tray.mode === 'fields' ? tray.keys()[i] : key,
        kind: policy.kind,
        file: null,
        url: '',
        name: '',
        size: 0,
        type: '',
        meta: {},
        state: 'restoring',
        uploadId: id,
        pendingId: null,
        check: null,
        restored: true,
      };
      live.add(item);
      return item;
    });
    tray.entries.push(...items);
    const failed = new Set<MediaItem>();
    /* видео: превью сервер не отдаёт (решение Q7-video) — загрузить заново, без запросов */
    if (policy.kind === 'video') items.forEach(item => failed.add(item));
    else
      try {
        const me = await endpoints.me();
        if (me.user.id !== owner) throw new Error('owner_changed');
        await Promise.all(
          items.map(async item => {
            try {
              const blob = await endpoints.uploadPreview(item.uploadId!, request.signal);
              if (request.signal.aborted) throw new Error('aborted');
              if (!PREVIEW[policy.kind].includes(blob.type) || !blob.size || blob.size > Math.max(policy.maxBytes, 20 * MB))
                throw new Error('preview_invalid');
              const file = new File([blob], uiText(policy.kind === 'audio' ? 'Запись' : 'Фото'), { type: blob.type });
              if (policy.kind === 'audio') {
                const ms = await mediaFile.readDuration(file, 'audio', request.signal);
                if (!ms) throw new Error('audio_preview_unreadable');
                item.meta.durationMs = ms;
              }
              Object.assign(item, { file, name: file.name, size: file.size, type: file.type });
            } catch {
              failed.add(item);
            }
          }),
        );
        const again = await endpoints.me();
        if (again.user.id !== owner) throw new Error('owner_changed');
      } catch {
        items.forEach(item => failed.add(item));
      }
    clearTimeout(deadline);
    if (readers.get(tray) === request) readers.delete(tray);
    /* cancel() или новое восстановление лотка — молча; запись, которую убрали или заменили, пока читалось, — тоже */
    if (mine !== epoch || (request.signal.aborted && !timedOut)) {
      items.forEach(forget);
      return;
    }
    const kept = items.filter(item => tray.entries.includes(item));
    for (const item of kept) {
      if (failed.has(item)) continue;
      item.url = url(item.file!);
      item.state = 'ready';
      o.put?.(item, tray.name);
      void checkOf(item, tray);
    }
    const lost = kept.filter(item => failed.has(item));
    if (lost.length) {
      lost.forEach(item => {
        tray.entries.splice(tray.entries.indexOf(item), 1);
        forget(item);
      });
      o.drop?.(null, key, tray.name);
      o.say(uiText(ids.length > 1 ? 'Некоторые файлы повтора больше недоступны. Загрузите их заново.' : RESTORE_FAIL[policy.kind]), key);
    }
    /* превью не вышло (решение Q7): загрузка снимается, прежний id не используется */
    tray.rewrite();
  }
  const rawId = (key: string, value: string | undefined) => {
    if (!value) return '';
    const field = studioFields(svc.get().form).find(item => item.key === key);
    if (field?.value_encoding !== 'json') return value;
    try {
      const id = JSON.parse(value) as unknown;
      return typeof id === 'string' ? id : '';
    } catch {
      return '';
    }
  };

  const single = (key: string) => trayOf([key]);
  return {
    pick: (key, file, meta) => single(key).pickSlots([file], 0, [meta]),
    async recorded(key, blob, maxMs) {
      const mine = epoch;
      let wav: File;
      try {
        wav = await recordingWav(blob instanceof File ? blob : new File([blob], 'recording', { type: blob.type }), maxMs);
      } catch {
        return o.say(uiText('Не удалось прочитать запись, попробуйте ещё раз'), key);
      }
      if (mine !== epoch) return;
      await add(single(key), [new File([wav], uiText('Запись с микрофона') + '.wav', { type: 'audio/wav' })], 0, [], true);
    },
    remove(key) {
      const tray = single(key);
      if (tray.entries[0]) tray.removeSlot(0);
      else if (!opLocked()) {
        if (tray.mode === 'fields') svc.actions.clearUpload(key);
        else writeKey(key, null);
      }
    },
    item: key => trays.get(key)?.entries[0] ?? null,
    restore: (key, uploadId) => single(key).restore([uploadId]),
    slots: trayOf,
    pickSlots: (files, keys, at, metas) => trayOf(keys).pickSlots(files, at, metas),
    removeSlot: (i, keys) => trayOf(keys).removeSlot(i),
    async resume() {
      if (opLocked()) {
        const { s, d } = now();
        return o.say(lockedText(s, d), [...trays.values()][0]?.keys()[0] ?? '');
      }
      for (const tray of trays.values())
        for (const item of tray.entries)
          if (item.state === 'pending' || item.state === 'held') {
            item.state = 'queued';
            queue.push({ item, tray });
          }
      await drain();
    },
    pending: () => [...trays.values()].some(tray => tray.entries.some(item => item.state !== 'ready')),
    stalled: () =>
      !flight &&
      !queue.length &&
      [...trays.values()].some(tray => tray.entries.some(item => item.state === 'pending' || item.state === 'held')),
    sync() {
      for (const tray of trays.values()) if (tray.dirty) tray.rewrite();
    },
    upload: detach,
    cancel() {
      epoch++;
      queue.length = 0;
      [...waiters.keys()].forEach(item => reply(item, { cancelled: true }));
      readers.forEach(request => request.abort());
      readers.clear();
      consentSheet?.close();
      consentSheet = null;
      for (const tray of trays.values()) tray.entries.splice(0).forEach(forget);
      live.clear();
      for (const value of owned) URL.revokeObjectURL(value);
      owned.clear();
    },
  };
}
